/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */

import axios, { RawAxiosRequestConfig, AxiosRequestTransformer, AxiosRequestHeaders } from "axios";
import { computed, ref, watch } from 'vue';
import { useAuth } from './auth';
import { ResponseError } from "@/types/ResponseError";
import moment from "moment";

export const AUTH_ERROR = 401;

// Lazy references to avoid circular dependency with app.ts
// These are set by app.ts after initialization
export let i18nRef: any = null;
export let routerRef: any = null;

export const setAppRefs = (i18n: any, router: any) => {
  i18nRef = i18n;
  routerRef = router;
};

export const useApi = () => {
  // console.log('useApi: ' + access_token);
  const { logout, getToken } = useAuth();
  const i18n = i18nRef;
  const router = routerRef;

  const dateTransformer = (data: any): any => {
    // Skip transformation for FormData
    if (data instanceof FormData) {
      return data;
    }
    if (data instanceof Date) {
      // do your specific formatting here
      return moment(data).format("YYYY-MM-DD HH:mm:ss");
      // return data.toLocaleString();
    }
    if (Array.isArray(data)) {
      return data.map(dateTransformer);
    }
    if (typeof data === "object" && data !== null) {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key,
          dateTransformer(value),
        ])
      );
    }
    return data;
  };

  // const { t } = i18n.global;
  const api = axios.create({
    transformRequest: [
      dateTransformer,
      ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
    ],
  });
  const t = (i18n?.global?.t ?? ((key: string) => key)) as (key: string) => string;

  const data = ref();
  const loading = ref(false);
  const error = ref();

  /**
   * Metoda koja postavlja defaultne headere za dohvat podataka. U headere se dodaje ApiToken
   * za autorizaciju na backendu.
   * @returns RawAxiosRequestConfig za dohvat podataka
   */
  const defaultHeaders = () => {
    let access_token = getToken();
    const axiosRequestConfig: RawAxiosRequestConfig = {
      headers: {
        ApiToken: access_token ? access_token : "",
      },
    };
    return axiosRequestConfig;
  };

  /**
   * Metoda za dohvat podataka preko GET poziva u blob formatu
   * @returns RawAxiosRequestConfig za dohvat podataka u blob formatu
   */
  const blobHeaders = () => {
    let access_token = getToken();
    const axiosRequestConfig: RawAxiosRequestConfig = {
      headers: {
        ApiToken: access_token ? access_token : "",
      },
      responseType: "blob",
    };
    return axiosRequestConfig;
  };

  /**
   * Metoda za dohvat podataka preko POST poziva
   * @param endpoint string route za dohvat podataka
   * @param payload Record<string, any> payload za POST poziv
   * @returns promise sa podacima
   */
  const post = (endpoint: string, payload?: Record<string, any>) => {
    loading.value = true;
    error.value = undefined;

    // Special handling for FormData
    const config = defaultHeaders();
    if (payload instanceof FormData) {
      // Let the browser set the Content-Type with boundary for multipart/form-data
      delete config.headers?.['Content-Type'];
    }

    return api
      .post(endpoint, payload, config)
      .then((res) => (data.value = res.data))
      .catch((e) => {
        error.value = e;

        throw e;
      })
      .finally(() => (loading.value = false));
  };

  /**
   * Metoda za dohvat podataka preko GET poziva
   * @param endpoint string route za dohvat podataka
   * @param query Record<string, any> query parametri za dohvat podataka
   * @returns promise sa podacima
   */
  const get = (endpoint: string, query?: Record<string, any>) => {
    loading.value = true;
    error.value = undefined;

    let queryString = "";

    if (query) {
      queryString =
        "?" +
        Object.entries(query)
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
          )
          .join("&");
    }

    return api
      .get(endpoint + queryString, defaultHeaders())
      .then((res) => (data.value = res.data))
      .catch((e) => {
        error.value = e;

        throw e;
      })
      .finally(() => (loading.value = false));
  };

  /**
   * Metoda za brisanje podataka preko DELETE poziva
   * @param endpoint string route za brisanje
   * @returns
   */
  const del = (endpoint: string) => {
    loading.value = true;
    error.value = undefined;

    return api
      .delete(endpoint, defaultHeaders())
      .then((res) => (data.value = res.data))
      .catch((e) => {
        error.value = e;

        throw e;
      })
      .finally(() => (loading.value = false));
  };

  /**
   * Metoda koja mijenja src atribut img HTML elementa tako da prikaže učitanu sliku dohvaćenu
   * kroz autoriziran GET poziv. Dohvati se blob i pomoću URL.createObjectURL se konvertira u
   * string za prikaz u img HTML elementu
   * @param endpoint putanja do slike za dohvat preko GET poziva
   * @param element img HTML element kojem treba promijeniti src atribut
   * @param spinner HTML objekt koji je prikazan za vrijeme učitavanja slike
   * @returns void
   */
  const imageSrc = (endpoint: string, element: any, spinner: any) => {
    loading.value = true;
    if (spinner) {
      spinner.style.display = "flex";
      // console.log('imageSrc');
      // console.log(spinner.style.display);
    }
    error.value = undefined;
    return api
      .get(endpoint, blobHeaders())
      .then((res) => {
        // console.log(res);
        const objectUrl = URL.createObjectURL(res.data);
        element.src = objectUrl;
        element.onload = () => URL.revokeObjectURL(objectUrl);
      })
      .catch((e) => {
        error.value = e;
        throw e;
      })
      .finally(() => {
        loading.value = false;
        if (spinner) {
          spinner.style.display = "none";
          // console.log('imageSrc');
          // console.log(spinner.style.display);
        }
      });
  };

  /**
   * Metoda za dohvat blob podataka preko autoriziranog GET poziva.
   * @param endpoint putanja za dohvat blob podataka
   * @returns promise sa Blob objektom
   */
  const getBlob = (endpoint: string): Promise<Blob> => {
    loading.value = true;
    error.value = undefined;
    return api
      .get(endpoint, blobHeaders())
      .then((res) => res.data as Blob)
      .catch((e) => {
        error.value = e;
        throw e;
      })
      .finally(() => (loading.value = false));
  };

  /**
   * Shortcut metoda koja samo služi jednostavnijemo pozivu imageSrc metode.
   * Provjerava neke preduvjete prije poziva imageSrc metode
   * @param el img HTML element kojem treba promijeniti src atribut
   * @param binding putanja do slike za dohvat preko GET poziva
   * @param spinner HTML objekt koji je prikazan za vrijeme učitavanja slike
   */
  const setImgSrc = (el: any, binding: any, spinner: any) => {
    if (binding.oldValue === undefined || binding.value !== binding.oldValue) {
      var imageUrl = binding.value;
      imageSrc(imageUrl, el, spinner);
    }
  };

  /**
   * Vraća poruku o grešci koja se prikazuje korisniku
   */
  const errorMessage = computed((): string | undefined => {
    // console.log('?? compute', error.value);
    if (error.value) {
      if (error.value?.response?.status === AUTH_ERROR)
        return t("login.unauthorized");
      return error.value.message;
    }
    return undefined;
  });

  /**
   * Vraća detalje o grešci koja se prikazuje korisniku
   */
  const errorDetails = computed((): string | undefined => {
    if (error.value && error.value.response) {
      if (error.value?.response?.status === AUTH_ERROR)
        return t("login.unauthorized_message");
      return error.value.response.data.message;
    }
    return undefined;
  });

  /**
   * Raščlanjuje grešku koja se vraća sa servera i vraća objekt sa poljima i porukama
   * kako bi mogli prikazati greške na formi. Greške backenda su prilagođene za ovakvo
   * parsiranje i vraćaju se u obliku:
   * {
   *  "status": false, "reason": "after", "message", "end start"],
   * }
   * Iz ovog primjera se generira objekt: ["end", "start"]
   */
  const errorFields = computed(() => {
    if (error.value && Array.isArray(error.value.response.data.message)) {
      return (error.value.response.data.message as string[]).reduce(
        (acc: Record<string, any>, msg: string) => {
          const [field] = msg.split(" ");

          if (!acc[field]) {
            acc[field] = [];
          }

          acc[field].push(msg);

          return acc;
        },
        {}
      );
    }
    return undefined;
  });

  /**
   * Metoda koja vraća CSS klase za prikaz grešaka na formi
   * @param key polje za koje se traže greške
   * @returns string[] CSS klase za prikaz grešaka na formi
   */
  const computedClasses = (key: string) => {
    if (errorFields.value?.hasOwnProperty(key)) {
      return ["border-red-600", "bg-red-200", "text-red-900"];
    }
    return ["border-grey-600", "bg-white", "text-gray-900"];
  };

  /**
   * Procesira bačenu iznimku sa servera. Ako je status 401, onda se ne radi ništa.
   * To radimo jer se u slučaju 401 iznimke, korisnik preusmjerava na login stranicu.
   * @param e Exception koja se vraća sa servera
   * @returns void
   */
  const processError = (e: any): void => {
    if (e.response.status == AUTH_ERROR) {
      return;
    }
    throw e;
  };

  /**
   * Watcher koji prati promjene u error objektu. Ako je status 401, onda se korisnik
   * preusmjerava na login stranicu.
   */
  watch([error], () => {
    // If 401 Unauthorised, force user to login
    console.log(error);
    if (error.value?.response?.status === AUTH_ERROR && router) {
      logout();
    }
  });

  return {
    loading,
    data,
    error,
    get,
    post,
    del,
    imageSrc,
    getBlob,
    setImgSrc,
    errorMessage,
    errorDetails,
    errorFields,
    computedClasses,
    processError,
  };
};

// export default api
